import React, { useEffect, useState } from 'react';
import LoadingSpinner from '../../components/common/LoadingSpinner/LoadingSpinner';
import KanbanBoard from '../../components/orders/KanbanBoard/KanbanBoard';
import OrderDetailModal from '../../components/orders/OrderDetailModal/OrderDetailModal';
import { USE_MOCK_API } from '../../config/api';
import mockOrdersApi from '../../services/api/mockOrdersApi';
import ordersApi from '../../services/api/ordersApi';
import { trackOrderView } from '../../utils/activityTracker';
import { isOrderLate } from '../../utils/orderUtils';
import './Orders.css';

// Choose API based on configuration
const api = USE_MOCK_API ? mockOrdersApi : ordersApi;

const Orders = () => {
  const [ordersByStatus, setOrdersByStatus] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [detailedOrder, setDetailedOrder] = useState(null);

  // Load orders on component mount
  useEffect(() => {
    loadOrders();
  }, []);

  const loadOrders = async () => {
    setLoading(true);
    setError('');

    try {
      const response = await api.getOrders();
      console.log('API Response:', response); // Debug log
      
      // Group orders by status (same logic as the API but also needed for UI)
      const ordersByStatus = {
        'Para fazer': [],
        'Aguardando Material': [],
        'Feito': [],
        'Aguardando Transporte': [],
        'Outro': []
      };

      // If API returns ordersByStatus (mock API), use it directly
      if (response.ordersByStatus) {
        console.log('Using mock API grouped response');
        // Calculate late orders for mock API response
        Object.keys(response.ordersByStatus).forEach(status => {
          response.ordersByStatus[status].forEach(order => {
            order.atrasado = isOrderLate(order);
          });
        });
        setOrdersByStatus(response.ordersByStatus);
      } else if (response.data) {
        // If API returns flat array (real API), group the orders
        console.log('Grouping orders from real API, total orders:', response.data.length);
        response.data.forEach(order => {
          // Calculate if order is late
          order.atrasado = isOrderLate(order);
          
          const status = order.statusOperacao || order.status || 'Outro';
          if (ordersByStatus[status]) {
            ordersByStatus[status].push(order);
          } else {
            ordersByStatus['Outro'].push(order);
          }
        });
        
        console.log('Grouped orders by status:', Object.keys(ordersByStatus).map(status => 
          `${status}: ${ordersByStatus[status].length}`
        ));
        
        setOrdersByStatus(ordersByStatus);
      } else {
        console.log('No data received, using empty structure');
        setOrdersByStatus(ordersByStatus);
      }
    } catch (err) {
      setError('Erro ao carregar os pedidos. Tente novamente.');
      console.error('Error loading orders:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleCardClick = async (order) => {
    setSelectedOrder(order);
    setIsModalOpen(true);
    
    // Load detailed order information
    try {
      const detailedOrderData = await api.getOrderDetails(order.idPed, order.tenantId);
      setDetailedOrder(detailedOrderData);
      trackOrderView(order.idPed, `#${order.idPedido} - ${order.nomeCliente}`, order.tenantId);
    } catch (err) {
      console.error('Error loading order details:', err);
      // Use the basic order data if detailed loading fails
      setDetailedOrder(order);
    }
  };

  const handleStatusChange = async (data) => {
    try {
      await api.updateOrderCheckbox(data);
      
      // Update local state
      setOrdersByStatus(prev => {
        const newState = { ...prev };
        Object.keys(newState).forEach(status => {
          const orderIndex = newState[status].findIndex(o => o.idPed === data.pedidoId);
          if (orderIndex !== -1) {
            const updatedOrder = { ...newState[status][orderIndex] };
            if (data.statusType === 'separacao') {
              updatedOrder.statusSeparacao = data.isChecked ? 'Concluído' : 'Em aberto';
            } else if (data.statusType === 'producao') {
              updatedOrder.statusProducao = data.isChecked ? 'Concluído' : 'Em aberto';
            }
            newState[status][orderIndex] = updatedOrder;
          }
        });
        return newState;
      });

      // Update detailed order if modal is open
      if (detailedOrder && detailedOrder.idPed === data.pedidoId) {
        setDetailedOrder(prev => ({
          ...prev,
          [data.statusType === 'separacao' ? 'statusSeparacao' : 'statusProducao']: 
            data.isChecked ? 'Concluído' : 'Em aberto'
        }));
      }
    } catch (err) {
      console.error('Error updating status:', err);
      throw err; // Re-throw to let the checkbox component handle the error
    }
  };

  const handleOrderStatusUpdate = async (data) => {
    try {
      await api.updateOrderStatus(data);
      
      // Update local state
      setOrdersByStatus(prev => {
        const newState = { ...prev };
        
        // Find the order in any status
        let orderToMove = null;
        let sourceStatus = null;
        
        Object.keys(newState).forEach(status => {
          const orderIndex = newState[status].findIndex(o => o.idPed === data.pedidoId);
          if (orderIndex !== -1) {
            orderToMove = { ...newState[status][orderIndex] };
            sourceStatus = status;
            // Remove from source status
            newState[status] = newState[status].filter(o => o.idPed !== data.pedidoId);
          }
        });
        
        if (orderToMove) {
          // Update order with new status and position
          const updatedOrder = {
            ...orderToMove,
            statusOperacao: data.status,
            tokenPrioridade: data.position
          };
          
          // Add to target status
          if (!newState[data.status]) {
            newState[data.status] = [];
          }
          newState[data.status].push(updatedOrder);
          
          // Sort by priority within the target status
          newState[data.status].sort((a, b) => a.tokenPrioridade - b.tokenPrioridade);
        }
        
        return newState;
      });
    } catch (err) {
      console.error('Error updating order status:', err);
      throw err;
    }
  };

  const handleSaveChanges = async (data) => {
    try {
      await api.saveOrderChanges(data);
      
      // Update detailed order with saved changes
      if (detailedOrder && detailedOrder.idPed === data.pedidoId) {
        setDetailedOrder(prev => ({
          ...prev,
          atualizacoesDeItens: data.changes
        }));
      }
    } catch (err) {
      console.error('Error saving changes:', err);
      throw err;
    }
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedOrder(null);
    setDetailedOrder(null);
  };

  if (loading) {
    return (
      <div className="orders-page">
        <div className="loading-container">
          <LoadingSpinner text="Carregando pedidos..." />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="orders-page">
        <div className="error-container">
          <div className="error-message">{error}</div>
          <button className="btn btn-primary" onClick={loadOrders}>
            Tentar Novamente
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="orders-page">
      <div className="orders-header">
        <h1>Kanban de Pedidos</h1>
        {USE_MOCK_API && (
          <div className="dev-indicator">
            <small>🔧 Usando dados de teste (Mock API)</small>
          </div>
        )}
      </div>

      <KanbanBoard
        ordersByStatus={ordersByStatus}
        onCardClick={handleCardClick}
        onStatusChange={handleStatusChange}
        onOrderStatusUpdate={handleOrderStatusUpdate}
      />

      <OrderDetailModal
        order={detailedOrder}
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        onStatusChange={handleStatusChange}
        onSaveChanges={handleSaveChanges}
      />
    </div>
  );
};

export default Orders;
